import React, { useState, type PropsWithChildren } from "react";
import { useQuery } from "@apollo/client";
import { GetBrochurePDFs, type GetBrochurePDFsData, type GetBrochurePDFsVariables } from "@graphql/resources/productsBrochures";
import Pagination from "@react/widget/Pagination";

export interface GetProductBrochuresLabel {
  loading: string; // Loading...
  getBrochure: string; // Get Brochure
  noResultsFound: string; // No results found.
  prev: string; // Prev
  next: string; // Next
}
export interface GetProductBrochuresProps extends PropsWithChildren {
  currentSite: Page.Site;
  title?: string;
  volume: string;
  limit?: number;
  page?: number;
  productServiceVal: string;
  brochureTypeVal: string;
  label: GetProductBrochuresLabel;
  // getBrochuresByPage?: (current: number) => void;
}

const GetProductBrochures: React.FC<GetProductBrochuresProps> = ({
  currentSite,
  title = "*",
  volume,
  limit = 9,
  page:_page = 1,
  productServiceVal,
  brochureTypeVal,
  label,
  // getBrochuresByPage = () => {},
}) => {
  const [page, setPage] = useState(_page);
  // const [data, setData] = useState<GetBrochurePDFsData>();
  const offset = (page - 1) * limit;
  // Apollo Query Hook
  const finialSite = ["au", "uk", "sg", "in"].includes(currentSite.locale) ? "default" : currentSite.handle;
  
  const getAllowedShowWebsite = (locale: string): (string | null)[] => {
    if (locale === "au") return ["allSites", "onlyAuSite", null];
    if (["uk", "sg", "in", "default"].includes(locale)) {
      return ["allSites", "otherEnglishSites", null];
    }
    return ["allSites", null];
  };

  const { loading, error, data, refetch, fetchMore } = useQuery<GetBrochurePDFsData, GetBrochurePDFsVariables>(GetBrochurePDFs, {
    variables: {
      site: finialSite,
      search: `title:${title}`,
      volume,
      limit,
      offset,
      relatedToEntries: productServiceVal === "*" ? null : [{"id": productServiceVal}],
      brochureType: brochureTypeVal === "*" ? null : brochureTypeVal,
      showWebsite: getAllowedShowWebsite(currentSite.locale),
    },
  });

  const search = new URLSearchParams(currentSite.search);
  if (search.has("refresh")) refetch();

  if (loading) return <p className="mt-6">{label.loading}</p>;
  if (error) return <p>Error: {error.message}</p>;

  // Data Handling
  const {brochures, assetCount} = data || {};
  if(!brochures || !assetCount) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 border-t pt-12">
        <p>{label.noResultsFound}</p>
      </div>
    );
  }
  const total = Math.ceil(assetCount / limit);


  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center [&amp;>div]:max-w-sm [&amp;>div]:w-full mt-12">
        {brochures.map(({ title, url }) => (
          <div
            key={title}
            className="flex bg-white flex-col text-left rounded-lg px-7 py-8 [&>p]:mb-6 border"
          >
            <svg className="img" width="80" height="80">
              <use xlinkHref="#resources-icon" />
              <symbol id="resources-icon" viewBox="0 0 80 80">
                <circle className="cls-1" cx="25.9" cy="24.2" r="4.9" />
                <rect x="21" y="35.9" width="39.4" height="4.4" />
                <rect x="21" y="48.4" width="39.4" height="4.4" />
                <rect x="21" y="60.9" width="39.4" height="4.4" />
                <path d="M71.5,18.2h0L53.6,1.2H9.8V79.2h61.7V18.2h0Zm-6.4,0h-11.5V7.2l11.5,11Zm2,56.7H14.1V5.5H49.2V22.5h17.9v52.3Z" />
              </symbol>
            </svg>
            <p className="font-semibold mt-6">{title}</p>
            <div className="mt-auto">
              <a
                className="btn flex justify-center font-semibold transition duration-300 cursor-pointer [&>*]:align-middle !pl-0 pr-8 hover:text-primary active:text-gray-900 [&_.icon]:text-primary group/btn text-left !h-fit md:!h- w-fit px-4 py-[5px] text-sm rounded-full"
                target="_blank"
                href={url}
              >
                <div className="h-full flex items-center justify-center">
                  <div className="bg-gray-50 btn-iconbox mr-5 flex items-center justify-center p-2 rounded-full transition-all duration-150 ease-in-out group-hover/btn:translate-x-1.5">
                    <svg className="icon btn-icon w-4 h-4 text-red-500 transition-all duration-300">
                      <use xlinkHref="#solid:arrow-right" />
                    </svg>
                  </div>
                  <span>{label.getBrochure}</span>
                </div>
              </a>
            </div>
          </div>
        ))}
      </div>
      <Pagination
        label={{
          prev: label.prev,
          next: label.next
        }}
        page={page}
        total={total}
        onChange={(e) => setPage(e)}
      />
    </>
  );
}
export default GetProductBrochures;